import React from 'react';
import { Plus, Mail } from 'lucide-react';
import { IntegrationCard, type IntegrationType, type IntegrationStatus } from '../../organisms/IntegrationCard';
import './IntegrationsPageLayout.css';

export type SyncScheduleMode = 'automatic' | 'daily' | 'weekly';

export interface Integration {
  id: string;
  type: IntegrationType;
  name: string;
  account: string;
  status: IntegrationStatus;
  lastSync?: string;
  lastCheckNewMessages?: number | null;
  error?: string | null;
  syncSchedule?: SyncScheduleMode;
  syncScheduleTime?: string | null;
  syncScheduleDay?: number | null;
  /** #1570: store the email body as a separate PDF during ingest (default off) */
  extractEmailBodyPdf?: boolean;
  /** #3035: the IMAP folders this mailbox polls (empty = polls nothing, rendered as a warning) */
  pollingFolders?: string[];
  /** #3184: wire → mUTF-7-decoded labels for `pollingFolders` (labels only; values stay wire) */
  pollingFolderDisplayNames?: Record<string, string>;
  /** #3035: non-fatal degradation codes (`no_polling_folders`, `sent_folder_undiscoverable`) */
  statusWarnings?: string[];
}

export interface IntegrationsPageLayoutProps {
  /** Page title */
  title?: string;
  /** Page description */
  description?: string;
  /** List of integrations to display */
  integrations: Integration[];
  /** ID of integration currently being checked (shows loading state) */
  checkingIntegrationId?: string | null;
  /**
   * IDs of ALL integrations currently being checked (#3431). Since check-now became
   * 202 + background, several mailboxes can genuinely be checking at once — a single id
   * would spin one card and leave the others showing an enabled button that 409s.
   * Unioned with `checkingIntegrationId`, which stays for callers that only ever have one.
   */
  checkingIntegrationIds?: readonly string[];
  /** Handler for "Add Email Inbox" button */
  onAddIntegration: () => void;
  /** Handler for "Check now" action on an integration */
  onCheckNow?: (id: string) => void;
  /** Handler for "Reconnect" action on an integration */
  onReconnect?: (id: string) => void;
  /** Handler for "Disconnect" action on an integration */
  onDisconnect?: (id: string) => void;
  /** Handler for schedule mode change */
  onScheduleChange?: (id: string, mode: SyncScheduleMode, time?: string, day?: number) => void;
  /** #1570: handler for the extract-email-body-PDF toggle. When omitted, the toggle is hidden. */
  onExtractEmailBodyPdfChange?: (id: string, enabled: boolean) => void;
  /**
   * #3035: called when the farmer opens the folder editor on integration `id`, so the host
   * can fetch that mailbox's tree. When omitted the folder summary is read-only.
   */
  onEditFolders?: (id: string) => void;
  /** #3035: called with the saved selection. */
  onFoldersChange?: (id: string, folders: string[]) => void;
  /**
   * #3035: the enumeration state for the ONE integration whose editor is open. Held here
   * rather than per-`Integration` because only one editor can be open at a time and each
   * fetch costs an IMAP connection — keeping it per-row would invite fetching all of them.
   */
  folderEditor?: {
    integrationId: string;
    availableFolders?: string[];
    /** #3184: wire → decoded labels over offered ∪ persisted names, same fetch. */
    displayNames?: Record<string, string>;
    isLoading?: boolean;
    errorMessage?: string | null;
  } | null;
  /** Optional custom className */
  className?: string;
  /** Labels for i18n support */
  labels?: IntegrationsPageLayoutLabels;
  /**
   * How this renders (#3480).
   *
   * `'page'` (default) keeps the historical behaviour: an `<h1>`, the page
   * container, and an empty state that speaks for the whole page. Existing
   * callers, stories and tests are unaffected.
   *
   * `'section'` demotes it to one section among several — `<section>` with an
   * `<h2>`, no page container — for use inside `IntegrationsPageShell`. That is
   * the fix for the defect where this template's `<h1>`, primary action and
   * empty state silently spoke on behalf of two unrelated features stacked
   * below it, producing a page that rendered "Noch keine Integrationen"
   * directly above a successfully processed import.
   */
  renderAs?: 'page' | 'section';
}

export interface IntegrationsPageLayoutLabels {
  title?: string;
  description?: string;
  addButton?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyButton?: string;
  integrationCard?: {
    checkNow?: string;
    checking?: string;
    reconnect?: string;
    disconnect?: string;
    lastChecked?: string;
    noNewMessages?: string;
    newMessagesReceived?: string;
    errorPrefix?: string;
    notCollecting?: string;
    /** #3517: badge text + explanation for a mailbox support has parked */
    paused?: string;
    pausedExplanation?: string;
    // Relative time labels
    never?: string;
    justNow?: string;
    minuteAgo?: string;
    minutesAgo?: string;
    hourAgo?: string;
    hoursAgo?: string;
    dayAgo?: string;
    daysAgo?: string;
    // Schedule labels
    scheduleLabel?: string;
    scheduleAutomatic?: string;
    scheduleDaily?: string;
    scheduleWeekly?: string;
    scheduleTimeLabel?: string;
    scheduleDayLabel?: string;
    scheduleDays?: string[];
    scheduleAutomaticDesc?: string;
    scheduleDailyDesc?: string;
    scheduleWeeklyDesc?: string;
    scheduleEdit?: string;
    scheduleDone?: string;
    // #1570: extract-email-body-PDF toggle label
    extractEmailBodyPdf?: string;
    // #3035: polled-folder coverage + editor labels
    foldersLabel?: string;
    foldersCoverage?: string;
    foldersNoneWarning?: string;
    /** #3545: the server refused to let us read one or more selected folders */
    foldersUnreadableWarning?: string;
    foldersEdit?: string;
    foldersSave?: string;
    foldersCancel?: string;
    foldersLoading?: string;
    foldersRequired?: string;
  };
}

const defaultLabels: Required<IntegrationsPageLayoutLabels> = {
  title: 'Integrations',
  description: 'Once connected, Hofsync takes care of incoming documents from this inbox.',
  addButton: 'Add Email Inbox',
  emptyTitle: 'No mailboxes connected yet',
  emptyDescription: 'Connect an email mailbox so Hofsync can pick up incoming documents from it automatically.',
  emptyButton: 'Connect your first mailbox',
  integrationCard: {},
};

/**
 * IntegrationsPageLayout - Page layout for managing integrations.
 * Displays a list of connected integrations with an empty state.
 */
export const IntegrationsPageLayout: React.FC<IntegrationsPageLayoutProps> = ({
  title,
  description,
  integrations,
  checkingIntegrationId,
  checkingIntegrationIds,
  onAddIntegration,
  onCheckNow,
  onReconnect,
  onDisconnect,
  onScheduleChange,
  onExtractEmailBodyPdfChange,
  onEditFolders,
  onFoldersChange,
  folderEditor,
  className = '',
  labels = {},
  renderAs = 'page',
}) => {
  const l = { ...defaultLabels, ...labels };
  const isSection = renderAs === 'section';
  // Not a hardcoded id: two `renderAs="section"` instances on one document —
  // trivially reachable via Storybook autodocs, which renders every story of a
  // component on a single page — would emit duplicate ids, and `aria-labelledby`
  // would then resolve both landmarks to the first heading (#3480 review).
  const headingId = `integrations-mailboxes-title-${React.useId()}`;
  const Root = (isSection ? 'section' : 'div') as 'section' | 'div';
  const Heading = (isSection ? 'h2' : 'h1') as 'h2' | 'h1';

  return (
    <Root
      className={`integrations-page-layout ${isSection ? 'integrations-page-layout--section' : ''} ${className}`.replace(/\s+/g, ' ').trim()}
      {...(isSection ? { 'aria-labelledby': headingId } : {})}
    >
      <div className="integrations-page-layout__container">
        {/* Header */}
        <div className="integrations-page-layout__header">
          <Heading
            id={isSection ? headingId : undefined}
            className="integrations-page-layout__title"
          >
            {title || l.title}
          </Heading>
          <button
            type="button"
            className="integrations-page-layout__add-button"
            onClick={onAddIntegration}
          >
            <Plus size={16} />
            {l.addButton}
          </button>
        </div>

        {/* Description */}
        <p className="integrations-page-layout__description">
          {description || l.description}
        </p>

        {/* Integration List */}
        {integrations.length > 0 && (
          <div className="integrations-page-layout__list">
            {integrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                id={integration.id}
                type={integration.type}
                name={integration.name}
                account={integration.account}
                status={integration.status}
                lastSync={integration.lastSync}
                lastCheckNewMessages={integration.lastCheckNewMessages}
                error={integration.error}
                isChecking={
                  checkingIntegrationId === integration.id ||
                  (checkingIntegrationIds?.includes(integration.id) ?? false)
                }
                syncSchedule={integration.syncSchedule}
                syncScheduleTime={integration.syncScheduleTime}
                syncScheduleDay={integration.syncScheduleDay}
                extractEmailBodyPdf={integration.extractEmailBodyPdf}
                pollingFolders={integration.pollingFolders}
                pollingFolderDisplayNames={integration.pollingFolderDisplayNames}
                statusWarnings={integration.statusWarnings}
                availableFolders={
                  folderEditor?.integrationId === integration.id ? folderEditor.availableFolders : undefined
                }
                folderDisplayNames={
                  folderEditor?.integrationId === integration.id ? folderEditor.displayNames : undefined
                }
                isLoadingFolders={
                  folderEditor?.integrationId === integration.id && !!folderEditor.isLoading
                }
                folderEnumerationError={
                  folderEditor?.integrationId === integration.id ? folderEditor.errorMessage : null
                }
                onCheckNow={onCheckNow}
                onReconnect={onReconnect}
                onDisconnect={onDisconnect}
                onScheduleChange={onScheduleChange}
                onExtractEmailBodyPdfChange={onExtractEmailBodyPdfChange}
                onEditFolders={onEditFolders}
                onFoldersChange={onFoldersChange}
                labels={l.integrationCard}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {integrations.length === 0 && (
          <div className="integrations-page-layout__empty">
            <div className="integrations-page-layout__empty-icon">
              <Mail size={24} />
            </div>
            <h3 className="integrations-page-layout__empty-title">{l.emptyTitle}</h3>
            <p className="integrations-page-layout__empty-description">
              {l.emptyDescription}
            </p>
            {/* Section mode still renders this alongside the header's add
                button — two controls for one action inside a ~672px card. The
                #3480 review flagged it; suppressing it was tried and reverted,
                because this CTA is the entry point the add-mailbox flow is
                actually driven from when there are no mailboxes. Which of the
                two should go is a product decision, not a review cleanup —
                tracked in #3514. */}
            <button
              type="button"
              className="integrations-page-layout__empty-button"
              onClick={onAddIntegration}
            >
              <Plus size={16} />
              {l.emptyButton}
            </button>
          </div>
        )}
      </div>
    </Root>
  );
};

export default IntegrationsPageLayout;
